"use client";

import posthog from 'posthog-js';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface PostHogFeatureFlagPayload {
  [key: string]: string | number | boolean;
}

interface PostHogContextType {
  isReady: boolean;
  getFeatureFlag: (flag: string) => boolean | undefined;
  getFeatureFlagPayload: (flag: string) => PostHogFeatureFlagPayload | undefined;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
}

const PostHogContext = createContext<PostHogContextType | null>(null);

interface PostHogProviderProps {
  children: ReactNode;
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize PostHog
    if (typeof window !== 'undefined' && !posthog.__loaded) {
      posthog.init('phc_D3oSIJkJqHSE5yMR7vourU79cvN1LB85JvPcKZgkTBU', {
        api_host: 'https://us.i.posthog.com',
        loaded: () => {
          setIsReady(true);
        },
        capture_pageview: false, // Disable automatic pageview capture
        disable_session_recording: false,
      });
    } else if (posthog.__loaded) {
      setIsReady(true);
    }
  }, []);

  const getFeatureFlag = (flag: string): boolean | undefined => {
    if (!isReady) return undefined;
    return posthog.isFeatureEnabled(flag);
  };

  const getFeatureFlagPayload = (flag: string): PostHogFeatureFlagPayload | undefined => {
    if (!isReady) return undefined;
    return posthog.getFeatureFlagPayload(flag) as PostHogFeatureFlagPayload | undefined;
  };

  const identify = (distinctId: string, properties?: Record<string, unknown>) => {
    if (isReady) {
      posthog.identify(distinctId, properties);
    }
  };

  const capture = (event: string, properties?: Record<string, unknown>) => {
    if (isReady) {
      posthog.capture(event, properties);
    }
  };

  return (
    <PostHogContext.Provider 
      value={{ 
        isReady, 
        getFeatureFlag, 
        getFeatureFlagPayload, 
        identify, 
        capture 
      }}
    >
      {children}
    </PostHogContext.Provider>
  );
};

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
};

export const useFeatureFlag = (flagName: string) => {
  const { isReady, getFeatureFlag } = usePostHog();
  const [flagValue, setFlagValue] = useState<boolean | undefined>(undefined);
  
  useEffect(() => {
    if (isReady) {
      const value = getFeatureFlag(flagName);
      setFlagValue(value);
    }
  }, [isReady, flagName, getFeatureFlag]);
  
  return { isLoading: !isReady, flagValue };
};

export const useFeatureFlagPayload = (flagName: string) => {
  const { isReady, getFeatureFlagPayload } = usePostHog();
  const [payload, setPayload] = useState<PostHogFeatureFlagPayload | undefined>(undefined);
  
  useEffect(() => {
    if (isReady) {
      const value = getFeatureFlagPayload(flagName);
      setPayload(value);
    }
  }, [isReady, flagName, getFeatureFlagPayload]);
  
  return { isLoading: !isReady, payload };
};